import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import SquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";

/* ------------------------------------------------------------------ */
/*  Design tokens — AI Logo Studio                                     */
/* ------------------------------------------------------------------ */
const tokens = {
  ink: "#14161F",
  inkSoft: "#6B7280",
  paper: "#F5F6F8",
  surface: "#FFFFFF",
  border: "#E5E7EC",
  violet: "#6C5CE7",
  violetSoft: "#EFEDFD",
  coral: "#FF5C7A",
  mint: "#008D4D",
  displayFont: '"Poppins", "Segoe UI", sans-serif',
  bodyFont: '"Inter", "Segoe UI", sans-serif',
  monoFont: '"JetBrains Mono", "Roboto Mono", monospace',
};

interface LogoDesignData {
  style: string;
  type: string;
  color: string;
  size: string;
  personality: string[];
  instructions: string;
}

interface Props {
  onGenerate: (data: LogoDesignData) => void;
}

const styles = [
  { label: "Minimal", icon: CircleOutlinedIcon },
  { label: "Modern", icon: SquareOutlinedIcon },
  { label: "Corporate", icon: WorkOutlineIcon },
  { label: "Luxury", icon: DiamondOutlinedIcon },
  { label: "Creative", icon: PaletteOutlinedIcon },
  { label: "Tech", icon: MemoryOutlinedIcon },
];

const colors = [
  { hex: "#2563EB", name: "Blue" },
  { hex: "#16A34A", name: "Green" },
  { hex: "#7C3AED", name: "Violet" },
  { hex: "#DC2626", name: "Red" },
  { hex: "#EA580C", name: "Orange" },
  { hex: "#111827", name: "Black" },
];

const sizes = [
  { value: "512x512", label: "512 × 512", caption: "Social profile" },
  { value: "1024x1024", label: "1024 × 1024", caption: "Standard use" },
  { value: "2048x2048", label: "2048 × 2048", caption: "Print-ready HD" },
];

const personalities = ["Professional", "Friendly", "Innovative", "Premium", "Bold"];

/* ------------------------------------------------------------------ */
/*  Small building blocks                                              */
/* ------------------------------------------------------------------ */
function StepLabel({ n, title, hint }: { n: number; title: string; hint?: string }) {
  return (
    <Box display="flex" alignItems="baseline" gap={1.25} mb={1.5}>
      <Typography
        sx={{
          fontFamily: tokens.monoFont,
          fontSize: 12,
          fontWeight: 700,
          color: tokens.violet,
          letterSpacing: 1,
        }}
      >
        {String(n).padStart(2, "0")}
      </Typography>
      <Typography sx={{ fontFamily: tokens.displayFont, fontWeight: 600, fontSize: 16, color: tokens.ink }}>
        {title}
      </Typography>
      {hint && (
        <Typography sx={{ fontFamily: tokens.bodyFont, fontSize: 12.5, color: tokens.inkSoft }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export default function LogoDesignForm({ onGenerate }: Props) {
  const [style, setStyle] = useState("Modern");
  const [type, setType] = useState("Icon + Text");
  const [size, setSize] = useState("1024x1024");
  const [color, setColor] = useState("#2563EB");
  const [personality, setPersonality] = useState<string[]>(["Professional"]);
  const [instructions, setInstructions] = useState("");

  const togglePersonality = (item: string) => {
    setPersonality((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const brandInitial = "A";

  return (
    <Box
      sx={{
        bgcolor: tokens.paper,
        p: { xs: 2, md: 4 },
        borderRadius: 4,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.3fr 1fr" },
        gap: 3,
        fontFamily: tokens.bodyFont,
      }}
    >
      {/* ---------------------------------------------------------- */}
      {/* LEFT: the form                                              */}
      {/* ---------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3,
          border: `1px solid ${tokens.border}`,
          bgcolor: tokens.surface,
        }}
      >
        {/* Eyebrow + heading */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: tokens.coral,
            }}
          />
          <Typography
            sx={{
              fontFamily: tokens.monoFont,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2,
              color: tokens.inkSoft,
              textTransform: "uppercase",
            }}
          >
            AI Logo Studio
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: tokens.displayFont,
            fontWeight: 700,
            fontSize: { xs: 22, md: 26 },
            color: tokens.ink,
            mb: 0.5,
          }}
        >
          Design your logo
        </Typography>
        <Typography sx={{ color: tokens.inkSoft, fontSize: 14, mb: 3.5 }}>
          Pick a direction below — the preview updates as you go.
        </Typography>

        {/* STEP 1 — Style */}
        <StepLabel n={1} title="Logo style" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.25,
            mb: 3.5,
          }}
        >
          {styles.map(({ label, icon: Icon }) => {
            const active = style === label;
            return (
              <Box
                key={label}
                onClick={() => setStyle(label)}
                sx={{
                  cursor: "pointer",
                  border: `1.5px solid ${active ? tokens.violet : tokens.border}`,
                  bgcolor: active ? tokens.violetSoft : tokens.surface,
                  borderRadius: 2,
                  py: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  transition: "all 0.15s ease",
                  "&:hover": { borderColor: tokens.violet },
                }}
              >
                <Icon sx={{ fontSize: 20, color: active ? tokens.violet : tokens.inkSoft }} />
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: active ? 700 : 500,
                    color: active ? tokens.violet : tokens.ink,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* STEP 2 — Type */}
        <StepLabel n={2} title="Logo type" />
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={type}
          onChange={(_e, value) => value && setType(value)}
          sx={{
            mb: 3.5,
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontFamily: tokens.bodyFont,
              fontWeight: 600,
              fontSize: 13,
              border: `1.5px solid ${tokens.border}`,
              color: tokens.inkSoft,
              "&.Mui-selected": {
                bgcolor: tokens.violet,
                color: "#fff",
                "&:hover": { bgcolor: tokens.violet },
              },
            },
          }}
        >
          <ToggleButton value="Icon + Text">Icon + Text</ToggleButton>
          <ToggleButton value="Icon Only">Icon Only</ToggleButton>
          <ToggleButton value="Text Only">Text Only</ToggleButton>
        </ToggleButtonGroup>

        {/* STEP 3 — Color */}
        <StepLabel n={3} title="Primary color" />
        <Box display="flex" gap={1.75} mb={3.5} flexWrap="wrap">
          {colors.map((c) => {
            const active = color === c.hex;
            return (
              <Box key={c.hex} display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                <Box
                  onClick={() => setColor(c.hex)}
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: c.hex,
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: active ? `2.5px solid ${tokens.ink}` : "2.5px solid transparent",
                    outline: `1px solid ${tokens.border}`,
                    outlineOffset: 2,
                    transition: "transform 0.15s ease",
                    "&:hover": { transform: "scale(1.08)" },
                  }}
                >
                  {active && <CheckIcon sx={{ fontSize: 16, color: "#fff" }} />}
                </Box>
                <Typography
                  sx={{ fontFamily: tokens.monoFont, fontSize: 10, color: tokens.inkSoft }}
                >
                  {c.name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* STEP 4 — Size */}
        <StepLabel n={4} title="Export size" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.25,
            mb: 3.5,
          }}
        >
          {sizes.map((s) => {
            const active = size === s.value;
            return (
              <Box
                key={s.value}
                onClick={() => setSize(s.value)}
                sx={{
                  cursor: "pointer",
                  border: `1.5px solid ${active ? tokens.violet : tokens.border}`,
                  bgcolor: active ? tokens.violetSoft : tokens.surface,
                  borderRadius: 2,
                  py: 1.25,
                  px: 1,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontFamily: tokens.monoFont, fontSize: 12.5, fontWeight: 700, color: active ? tokens.violet : tokens.ink }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: tokens.inkSoft, mt: 0.25 }}>
                  {s.caption}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* STEP 5 — Personality */}
        <StepLabel n={5} title="Brand personality" hint="pick as many as fit" />
        <Box display="flex" gap={1} flexWrap="wrap" mb={3.5}>
          {personalities.map((item) => {
            const active = personality.includes(item);
            return (
              <Chip
                key={item}
                label={item}
                clickable
                onClick={() => togglePersonality(item)}
                icon={active ? <CheckIcon sx={{ fontSize: 15 }} /> : undefined}
                sx={{
                  fontWeight: 600,
                  fontFamily: tokens.bodyFont,
                  bgcolor: active ? tokens.ink : tokens.paper,
                  color: active ? "#fff" : tokens.inkSoft,
                  border: `1px solid ${active ? tokens.ink : tokens.border}`,
                  "& .MuiChip-icon": { color: "#fff" },
                  "&:hover": { bgcolor: active ? tokens.ink : tokens.violetSoft },
                }}
              />
            );
          })}
        </Box>

        {/* STEP 6 — Instructions */}
        <StepLabel n={6} title="Anything specific?" hint="optional" />
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="e.g. a modern blue logo with an AI circuit icon and clean typography"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          sx={{
            mb: 4,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: 14,
              "& fieldset": { borderColor: tokens.border },
              "&:hover fieldset": { borderColor: tokens.violet },
              "&.Mui-focused fieldset": { borderColor: tokens.violet },
            },
          }}
        />

        <Button
          fullWidth
          size="large"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => onGenerate({ style, type, color, size, personality, instructions })}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontFamily: tokens.displayFont,
            fontWeight: 700,
            fontSize: 15,
            textTransform: "none",
            color: "#fff",
            background: `linear-gradient(135deg, ${tokens.violet}, ${tokens.coral})`,
            boxShadow: "0 8px 20px -6px rgba(108,92,231,0.55)",
            "&:hover": {
              background: `linear-gradient(135deg, ${tokens.violet}, ${tokens.coral})`,
              boxShadow: "0 10px 24px -6px rgba(108,92,231,0.7)",
            },
          }}
        >
          Generate logo
        </Button>
      </Paper>

      {/* ---------------------------------------------------------- */}
      {/* RIGHT: live preview                                         */}
      {/* ---------------------------------------------------------- */}
      <Box sx={{ position: { md: "sticky" }, top: { md: 24 }, alignSelf: "start" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${tokens.border}`,
            overflow: "hidden",
            bgcolor: tokens.surface,
          }}
        >
          {/* canvas area with dot grid */}
          <Box
            sx={{
              position: "relative",
              height: 260,
              backgroundColor: tokens.ink,
              backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "16px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography
              sx={{
                position: "absolute",
                top: 14,
                left: 16,
                fontFamily: tokens.monoFont,
                fontSize: 10,
                letterSpacing: 1.5,
                color: "rgba(255,255,255,0.45)",
                textTransform: "uppercase",
              }}
            >
              Live preview
            </Typography>

            {type !== "Text Only" && (
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  borderRadius: style === "Minimal" || style === "Luxury" ? "50%" : "22%",
                  bgcolor: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 12px 30px -8px ${color}99`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: tokens.displayFont,
                    fontWeight: 700,
                    fontSize: 34,
                    color: "#fff",
                  }}
                >
                  {brandInitial}
                </Typography>
              </Box>
            )}

            {type !== "Icon Only" && (
              <Typography
                sx={{
                  fontFamily: tokens.displayFont,
                  fontWeight: 700,
                  fontSize: 20,
                  color: "#fff",
                  letterSpacing: 0.5,
                }}
              >
                Acme<Box component="span" sx={{ color }}>Brand</Box>
              </Typography>
            )}
          </Box>

          {/* spec sheet */}
          <Box sx={{ p: 2.5 }}>
            <Typography
              sx={{
                fontFamily: tokens.monoFont,
                fontSize: 10,
                letterSpacing: 1.5,
                color: tokens.inkSoft,
                textTransform: "uppercase",
                mb: 1.25,
              }}
            >
              Spec sheet
            </Typography>

            {[
              ["Style", style],
              ["Type", type],
              ["Color", color.toUpperCase()],
              ["Size", size],
            ].map(([k, v]) => (
              <Box
                key={k}
                display="flex"
                justifyContent="space-between"
                sx={{ py: 0.9, borderBottom: `1px dashed ${tokens.border}` }}
              >
                <Typography sx={{ fontSize: 13, color: tokens.inkSoft }}>{k}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, fontFamily: tokens.monoFont }}>
                  {v}
                </Typography>
              </Box>
            ))}

            <Box sx={{ mt: 1.5, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {personality.length > 0 ? (
                personality.map((p) => (
                  <Box
                    key={p}
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      px: 1.1,
                      py: 0.4,
                      borderRadius: 5,
                      bgcolor: tokens.violetSoft,
                      color: tokens.violet,
                    }}
                  >
                    {p}
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: 12, color: tokens.inkSoft }}>
                  No personality traits selected yet
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        <Typography sx={{ fontSize: 12, color: tokens.inkSoft, mt: 1.5, px: 0.5 }}>
          This is a stylised preview — the AI-generated logo may differ once created.
        </Typography>
      </Box>
    </Box>
  );
}